import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  InternationalReturnsFlatTeamSettingGroupById,
  type InternationalReturnsFlatTeamSettingGroupId,
} from "./groups";

type InternationalReturnsSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: InternationalReturnsSettingInput,
): InternationalReturnsSettingInput {
  return metadata;
}

function internationalReturnsGroup<
  const Group extends InternationalReturnsFlatTeamSettingGroupId,
  const T extends Record<string, InternationalReturnsSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const InternationalReturnsRegistry = {
  ...internationalReturnsGroup("international_returns.redo_assisted_customs", {
    "returns/redoAssistedCustoms/hsCodeOptOut": setting({
      label: "Redo-assisted HS codes",
      description:
        "When enabled, Redo assigns an HS code to products that don't have one for cross-border return customs.",
      synonyms: [
        "hs code",
        "harmonized code",
        "redo assisted hs code",
        "customs classification",
        "assign hs codes",
        "automatic hs codes",
        "hs code assignment",
      ],
    }),
    "returns/redoAssistedCustoms/customsDescriptionOptOut": setting({
      label: "Redo-assisted customs descriptions",
      description:
        "When enabled, Redo writes a customs description for products that don't have one for cross-border return customs.",
      synonyms: [
        "customs description",
        "redo assisted customs description",
        "customs data",
      ],
    }),
  }),
  ...internationalReturnsGroup("international_returns.customs_metafields", {
    "returns/shopifyCustomsMetafields/descriptionKey": setting({
      label: "Product description metafield",
      description:
        "Shopify product metafield (namespace.key) used as the customs item description on international return labels.",
      synonyms: [
        "product description metafield",
        "customs description metafield",
      ],
    }),
    "returns/shopifyCustomsMetafields/manufacturerKey": setting({
      label: "Product manufacturer metafield",
      description:
        "Shopify product metafield (namespace.key) used as the manufacturer on the customs form for international return labels.",
      synonyms: [
        "product manufacturer metafield",
        "customs manufacturer metafield",
      ],
    }),
    "returns/shopifyCustomsMetafields/manufacturerPartNumberKey": setting({
      label: "Product manufacturer part number metafield",
      description:
        "Shopify product metafield (namespace.key) holding the manufacturer's part number. Declared to EU customs as the manufacturer Product Identifier, required on B2C imports from November 2026.",
      synonyms: [
        "product manufacturer part number",
        "manufacturer part number metafield",
        "mpn metafield",
      ],
    }),
    "returns/shopifyCustomsMetafields/materialKey": setting({
      label: "Product material metafield",
      description:
        "Shopify product metafield (namespace.key) appended to the customs item description for international return labels.",
      synonyms: ["product material metafield", "customs material metafield"],
    }),
    "returns/shopifyCustomsMetafields/variantDescriptionKey": setting({
      label: "Variant description metafield",
      description:
        "Shopify variant metafield (namespace.key) used as the customs item description; takes precedence over the product metafield.",
      synonyms: [
        "variant description metafield",
        "customs variant description metafield",
      ],
    }),
    "returns/shopifyCustomsMetafields/variantManufacturerKey": setting({
      label: "Variant manufacturer metafield",
      description:
        "Shopify variant metafield (namespace.key) used as the manufacturer on the customs form; takes precedence over the product metafield.",
      synonyms: [
        "variant manufacturer metafield",
        "customs variant manufacturer metafield",
      ],
    }),
    "returns/shopifyCustomsMetafields/variantManufacturerPartNumberKey":
      setting({
        label: "Variant manufacturer part number metafield",
        description:
          "Shopify variant metafield (namespace.key) holding the manufacturer's part number. Takes precedence over the product-level metafield.",
        synonyms: ["variant manufacturer part number"],
      }),
    "returns/shopifyCustomsMetafields/variantMaterialKey": setting({
      label: "Variant material metafield",
      description:
        "Shopify variant metafield (namespace.key) appended to the customs item description; takes precedence over the product metafield.",
      synonyms: [
        "variant material metafield",
        "customs variant material metafield",
      ],
    }),
  }),
};

type InternationalReturnsFlatTeamSettingKey =
  keyof typeof InternationalReturnsRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const InternationalReturnsFlatTeamSettingKeys = Object.keys(
  InternationalReturnsRegistry,
) as InternationalReturnsFlatTeamSettingKey[];

export const InternationalReturnsFlatTeamSettingCatalogDefinitions =
  InternationalReturnsFlatTeamSettingKeys.map((key) => {
    const definition = InternationalReturnsRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: InternationalReturnsFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    InternationalReturnsFlatTeamSettingKey,
    InternationalReturnsFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<InternationalReturnsFlatTeamSettingKey>
  >[];

export const InternationalReturnsFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    InternationalReturnsFlatTeamSettingCatalogDefinitions.map((definition) => [
      definition.id,
      definition.metadata,
    ]),
  ) as Record<InternationalReturnsFlatTeamSettingKey, SettingMetadata>;
